import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, type ReactNode } from "react";
import { DEFAULT_SETTINGS, createProject } from "./defaults";
import { cloneProject, initialState, loadState, mergeImportedProjects, saveState } from "./storage";
import type { AddonItem, AppState, PricingBackupV1, PricingProject, PricingSettings, WbsItem } from "./types";

type Action =
  | { type: "set-active"; id: string }
  | { type: "add-project"; project: PricingProject }
  | { type: "duplicate-project"; id: string }
  | { type: "delete-project"; id: string }
  | { type: "update-project"; id: string; patch: Partial<PricingProject> }
  | { type: "update-wbs"; projectId: string; itemId: string; patch: Partial<WbsItem> }
  | { type: "replace-wbs"; projectId: string; items: WbsItem[] }
  | { type: "update-addon"; projectId: string; itemId: string; patch: Partial<AddonItem> }
  | { type: "replace-addons"; projectId: string; items: AddonItem[] }
  | { type: "update-settings"; patch: Partial<PricingSettings> }
  | { type: "reset-settings" }
  | { type: "import-merge"; backup: PricingBackupV1 }
  | { type: "import-replace"; backup: PricingBackupV1 };

const stamp = () => new Date().toISOString();

function touchProject(project: PricingProject, patch: Partial<PricingProject> = {}) {
  return { ...project, ...patch, updatedAt: stamp() };
}

function reducer(state: AppState, action: Action): AppState {
  const savedAt = stamp();
  switch (action.type) {
    case "set-active":
      return { ...state, activeProjectId: action.id, savedAt };
    case "add-project":
      return { ...state, projects: [...state.projects, action.project], activeProjectId: action.project.id, savedAt };
    case "duplicate-project": {
      const source = state.projects.find((project) => project.id === action.id);
      if (!source) return state;
      const copy = cloneProject(source);
      return { ...state, projects: [...state.projects, copy], activeProjectId: copy.id, savedAt };
    }
    case "delete-project": {
      const remaining = state.projects.filter((project) => project.id !== action.id);
      const projects = remaining.length ? remaining : [createProject("新报价项目")];
      return { ...state, projects, activeProjectId: projects[0].id, savedAt };
    }
    case "update-project":
      return { ...state, projects: state.projects.map((project) => (project.id === action.id ? touchProject(project, action.patch) : project)), savedAt };
    case "update-wbs":
      return {
        ...state,
        projects: state.projects.map((project) =>
          project.id === action.projectId
            ? touchProject(project, { wbsItems: project.wbsItems.map((item) => (item.id === action.itemId ? { ...item, ...action.patch } : item)) })
            : project,
        ),
        savedAt,
      };
    case "replace-wbs":
      return {
        ...state,
        projects: state.projects.map((project) => (project.id === action.projectId ? touchProject(project, { wbsItems: action.items }) : project)),
        savedAt,
      };
    case "update-addon":
      return {
        ...state,
        projects: state.projects.map((project) =>
          project.id === action.projectId
            ? touchProject(project, { addonItems: project.addonItems.map((item) => (item.id === action.itemId ? { ...item, ...action.patch } : item)) })
            : project,
        ),
        savedAt,
      };
    case "replace-addons":
      return {
        ...state,
        projects: state.projects.map((project) => (project.id === action.projectId ? touchProject(project, { addonItems: action.items }) : project)),
        savedAt,
      };
    case "update-settings":
      return { ...state, settings: { ...state.settings, ...action.patch }, savedAt };
    case "reset-settings":
      return { ...state, settings: structuredClone(DEFAULT_SETTINGS), savedAt };
    case "import-merge": {
      const projects = mergeImportedProjects(state.projects, action.backup.projects);
      return { ...state, projects, settings: action.backup.settings, activeProjectId: projects.at(-1)?.id ?? state.activeProjectId, savedAt };
    }
    case "import-replace":
      return { settings: action.backup.settings, projects: action.backup.projects, activeProjectId: action.backup.activeProjectId, savedAt };
    default:
      return state;
  }
}

type StoreValue = {
  state: AppState;
  activeProject: PricingProject;
  dispatch: React.Dispatch<Action>;
  createNewProject: () => PricingProject;
  updateActiveProject: (patch: Partial<PricingProject>) => void;
};

const StoreContext = createContext<StoreValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, () => (typeof localStorage === "undefined" ? initialState() : loadState()));
  const activeProject = state.projects.find((project) => project.id === state.activeProjectId) ?? state.projects[0];

  useEffect(() => {
    saveState(state);
  }, [state]);

  const createNewProject = useCallback(() => {
    const project = createProject(`新报价项目 ${state.projects.length + 1}`);
    project.quoteNumber = `HZ-${project.quoteDate.replaceAll("-", "")}-${String(state.projects.length + 1).padStart(3, "0")}`;
    dispatch({ type: "add-project", project });
    return project;
  }, [state.projects.length]);

  const updateActiveProject = useCallback(
    (patch: Partial<PricingProject>) => dispatch({ type: "update-project", id: activeProject.id, patch }),
    [activeProject.id],
  );

  const value = useMemo(() => ({ state, activeProject, dispatch, createNewProject, updateActiveProject }), [state, activeProject, createNewProject, updateActiveProject]);
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function usePricingStore() {
  const value = useContext(StoreContext);
  if (!value) throw new Error("usePricingStore must be used inside StoreProvider");
  return value;
}
